/**
 * SoloSingleFileScreenRecording - Records screen video + optional system audio to a single file.
 *
 * Key features:
 * - Uses native MediaRecorder API for reliable background tab recording
 * - Streams chunks directly into OPFS while recording
 * - Final file is ready immediately on stop (no stitching)
 */

export interface SoloSingleFileScreenRecordingOptions {
    trackId: string;
    screenStream: MediaStream;
    additionalAudioStream?: MediaStream; // optional mic stream to mix into screen audio
    title?: string;
    projectId?: string;
    videoBitrate?: number;          // video bitrate in bps (default: 12_000_000)
    audioBitrate?: number;          // audio bitrate in bps (default: 192_000)
}

type RecordingState = 'idle' | 'recording' | 'stopping' | 'stopped';

const DEFAULT_VIDEO_BITRATE = 12_000_000; // 12 Mbps (screen detail)
const DEFAULT_AUDIO_BITRATE = 192_000;    // 192 kbps
const RECORDING_TIMESLICE_MS = 10_000;

const PREFERRED_MIME_TYPES = [
    // MP4 (Safari / some Chromium configs)
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4;codecs=avc1.4D401E,mp4a.40.2',
    'video/mp4;codecs=avc1.64001E,mp4a.40.2',
    'video/mp4;codecs=h264,aac',
    'video/mp4;codecs=avc1,mp4a',
    'video/mp4',

    // WebM (Chromium/Firefox)
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
];

function getSupportedMimeType(): string {
    for (const mimeType of PREFERRED_MIME_TYPES) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
            return mimeType;
        }
    }
    return '';
}

function extensionFromMime(mimeType: string): string {
    const normalized = (mimeType || '').toLowerCase();
    if (normalized.includes('video/mp4')) return 'mp4';
    if (normalized.includes('video/webm')) return 'webm';
    return 'webm';
}

function computeDefaultScreenBitrate(stream: MediaStream): number {
    const track = stream.getVideoTracks()[0];
    const settings = track?.getSettings?.();
    const width = settings?.width ?? 0;
    const height = settings?.height ?? 0;
    const frameRate = settings?.frameRate ?? 30;
    const pixels = width > 0 && height > 0 ? width * height : 0;

    let bitrate = DEFAULT_VIDEO_BITRATE;
    if (pixels >= 3840 * 2160) {
        bitrate = 35_000_000;
    } else if (pixels >= 2560 * 1440) {
        bitrate = 22_000_000;
    } else if (pixels >= 1920 * 1080) {
        bitrate = 14_000_000;
    } else if (pixels >= 1280 * 720) {
        bitrate = 8_000_000;
    } else if (pixels > 0) {
        bitrate = 5_000_000;
    }

    if (frameRate && frameRate > 30) {
        const scale = Math.min(2, frameRate / 30);
        bitrate = Math.round(bitrate * scale);
    }

    return bitrate;
}

const soloScreenRecordingPath = (trackId: string, ext: string): string =>
    `solo-recordings/${trackId}_screen.${ext}`;

async function getOpfsRoot(): Promise<FileSystemDirectoryHandle> {
    return await navigator.storage.getDirectory();
}

async function createOpfsWritableFile(path: string): Promise<{
    fileHandle: FileSystemFileHandle;
    writable: FileSystemWritableFileStream;
}> {
    const parts = path.split('/').filter(Boolean);
    const fileName = parts.pop()!;

    let dir = await getOpfsRoot();
    for (const segment of parts) {
        dir = await dir.getDirectoryHandle(segment, { create: true });
    }

    const fileHandle = await dir.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();

    return { fileHandle, writable };
}

async function readOpfsFile(path: string, desiredMimeType: string): Promise<File> {
    const parts = path.split('/').filter(Boolean);
    const fileName = parts.pop()!;

    let dir = await getOpfsRoot();
    for (const segment of parts) {
        dir = await dir.getDirectoryHandle(segment, { create: false });
    }

    const fileHandle = await dir.getFileHandle(fileName, { create: false });
    const file = await fileHandle.getFile();

    if (!file.type || file.type === 'application/octet-stream') {
        const buf = await file.arrayBuffer();
        return new File([buf], fileName, { type: desiredMimeType || 'video/webm' });
    }

    return file;
}

async function deleteOpfsFile(path: string): Promise<void> {
    const parts = path.split('/').filter(Boolean);
    const fileName = parts.pop()!;

    let dir = await getOpfsRoot();
    for (const segment of parts) {
        try {
            dir = await dir.getDirectoryHandle(segment, { create: false });
        } catch {
            return;
        }
    }

    try {
        await dir.removeEntry(fileName);
    } catch {
        // ignore
    }
}

export class SoloSingleFileScreenRecording {
    private trackId: string;
    private screenStream: MediaStream;
    private additionalAudioStream?: MediaStream;
    private title?: string;
    private projectId?: string;
    private videoBitrate: number;
    private audioBitrate: number;

    private state: RecordingState = 'idle';
    private stream: MediaStream | null = null;
    private mediaRecorder: MediaRecorder | null = null;
    private chunkCount: number = 0;
    private startTime = 0;

    private writable: FileSystemWritableFileStream | null = null;
    private opfsPath = '';
    private preferredMimeType = '';
    private actualMimeType = '';
    private pendingWrite: Promise<void> = Promise.resolve();

    // Optional audio mixing state (system + mic)
    private audioContext: AudioContext | null = null;
    private audioSourceNodes: MediaStreamAudioSourceNode[] = [];
    private audioDestination: MediaStreamAudioDestinationNode | null = null;

    constructor(options: SoloSingleFileScreenRecordingOptions) {
        this.trackId = options.trackId;
        this.screenStream = options.screenStream;
        this.additionalAudioStream = options.additionalAudioStream;
        this.title = options.title;
        this.projectId = options.projectId;
        this.videoBitrate = options.videoBitrate ?? computeDefaultScreenBitrate(this.screenStream);
        this.audioBitrate = options.audioBitrate ?? DEFAULT_AUDIO_BITRATE;
        this.preferredMimeType = getSupportedMimeType();
        this.opfsPath = soloScreenRecordingPath(this.trackId, extensionFromMime(this.preferredMimeType));
    }

    public async start(): Promise<void> {
        if (this.state !== 'idle') {
            console.warn('[SoloSingleFileScreenRecording] Cannot start, already recording');
            return;
        }

        const screenVideoTrack = this.screenStream.getVideoTracks()[0];
        if (!screenVideoTrack) {
            throw new Error('No screen video track available');
        }

        // Build audio for screen recording:
        // - Always include any audio track(s) from the screen stream (best-effort)
        // - Optionally mix in mic stream (if provided)
        const screenAudioTracks = this.screenStream.getAudioTracks();
        const additionalAudioTracks = this.additionalAudioStream?.getAudioTracks() || [];

        let audioTracksForRecording: MediaStreamTrack[] = [];
        if (screenAudioTracks.length > 0 && additionalAudioTracks.length > 0) {
            // Mix system + mic into a single track for consistent recorder behavior
            this.audioContext = new AudioContext({ latencyHint: 'playback' });
            try {
                await this.audioContext.resume();
            } catch { }
            this.audioDestination = this.audioContext.createMediaStreamDestination();

            const addSource = (stream: MediaStream) => {
                const source = this.audioContext!.createMediaStreamSource(stream);
                source.connect(this.audioDestination!);
                this.audioSourceNodes.push(source);
            };

            addSource(new MediaStream(screenAudioTracks));
            addSource(new MediaStream(additionalAudioTracks));

            audioTracksForRecording = this.audioDestination.stream.getAudioTracks();
        } else if (screenAudioTracks.length > 0) {
            audioTracksForRecording = screenAudioTracks;
        } else if (additionalAudioTracks.length > 0) {
            audioTracksForRecording = additionalAudioTracks;
        }

        this.stream = new MediaStream([
            screenVideoTrack,
            ...audioTracksForRecording,
        ]);

        // Log actual settings
        console.log('[SoloSingleFileScreenRecording] Recording stream prepared:', {
            video: screenVideoTrack.getSettings(),
            audioTracks: audioTracksForRecording.length,
        });

        const options: MediaRecorderOptions = {
            videoBitsPerSecond: this.videoBitrate,
            audioBitsPerSecond: this.audioBitrate,
        };
        if (this.preferredMimeType) {
            options.mimeType = this.preferredMimeType;
        }

        this.mediaRecorder = new MediaRecorder(this.stream, options);
        this.actualMimeType = this.mediaRecorder.mimeType || this.preferredMimeType || 'video/webm';
        this.chunkCount = 0;

        // If browser chose a different container than our path extension, update to match.
        // This keeps downstream metadata extraction consistent.
        const desiredExt = extensionFromMime(this.actualMimeType);
        const desiredPath = soloScreenRecordingPath(this.trackId, desiredExt);
        if (desiredPath !== this.opfsPath) {
            this.opfsPath = desiredPath;
        }

        const { writable } = await createOpfsWritableFile(this.opfsPath);
        this.writable = writable;

        this.mediaRecorder.ondataavailable = (event) => {
            if (event.data.size <= 0) return;
            this.chunkCount += 1;

            this.pendingWrite = this.pendingWrite.then(async () => {
                try {
                    await this.writable?.write(event.data);
                } catch (e) {
                    console.error('[SoloSingleFileScreenRecording] Error writing to OPFS:', e);
                }
            });

            console.log('[SoloSingleFileScreenRecording] Chunk recorded:', {
                size: event.data.size,
                totalChunks: this.chunkCount,
                isBackground: document.visibilityState === 'hidden',
            });
        };

        this.mediaRecorder.onerror = (event) => {
            console.error('[SoloSingleFileScreenRecording] MediaRecorder error:', event);
        };

        this.startTime = Date.now();
        this.mediaRecorder.start(RECORDING_TIMESLICE_MS);
        this.state = 'recording';

        console.log('[SoloSingleFileScreenRecording] Recording started', {
            trackId: this.trackId,
            mimeType: this.actualMimeType,
            videoBitrate: this.videoBitrate,
            audioBitrate: this.audioBitrate,
        });
    }

    public async stop(): Promise<{ trackId: string; filePath: string; file: File }> {
        if (this.state !== 'recording') {
            console.warn('[SoloSingleFileScreenRecording] Cannot stop, not recording');
            throw new Error('Not recording');
        }

        this.state = 'stopping';
        const duration = Date.now() - this.startTime;

        await new Promise<void>((resolve) => {
            if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
                resolve();
                return;
            }

            this.mediaRecorder.onstop = () => {
                resolve();
            };
            try {
                this.mediaRecorder.requestData();
            } catch (err) {
                console.warn('[SoloSingleFileScreenRecording] MediaRecorder.requestData() failed:', err);
            }
            this.mediaRecorder.stop();
        });

        // Wait for any pending writes. Some browsers may deliver a final `dataavailable` after
        // `stop`, so wait until chunk count stabilizes across a tick.
        const waitStart = Date.now();
        const maxWaitMs = 5_000;
        let lastChunkCount = this.chunkCount;
        try {
            while (true) {
                await this.pendingWrite;
                await new Promise<void>((r) => setTimeout(r, 0));
                if (this.chunkCount === lastChunkCount) break;
                lastChunkCount = this.chunkCount;
                if (Date.now() - waitStart > maxWaitMs) {
                    console.warn('[SoloSingleFileScreenRecording] Timed out waiting for final chunk writes', {
                        lastChunkCount,
                        currentChunkCount: this.chunkCount,
                    });
                    break;
                }
            }
        } catch (e) {
            console.error('[SoloSingleFileScreenRecording] Error in pending writes:', e);
        }

        if (this.writable) {
            try {
                await this.writable.close();
            } catch (e) {
                console.error('[SoloSingleFileScreenRecording] Error closing writable:', e);
            }
            this.writable = null;
        }

        // Cleanup any mixing graph
        try {
            this.audioSourceNodes.forEach((n) => n.disconnect());
        } catch { }
        this.audioSourceNodes = [];
        this.audioDestination = null;
        if (this.audioContext) {
            try {
                await this.audioContext.close();
            } catch { }
            this.audioContext = null;
        }

        this.stream = null;
        this.mediaRecorder = null;
        this.state = 'stopped';

        const file = await readOpfsFile(this.opfsPath, this.actualMimeType);

        console.log('[SoloSingleFileScreenRecording] Recording complete', {
            trackId: this.trackId,
            duration,
            fileSize: file.size,
            fileType: file.type,
            path: this.opfsPath,
        });

        return {
            trackId: this.trackId,
            filePath: this.opfsPath,
            file,
        };
    }

    /**
     * Pause recording (best-effort). MediaRecorder will stop emitting data while paused.
     */
    public async pause(): Promise<void> {
        if (this.state !== 'recording') return;
        const recorder = this.mediaRecorder;
        if (recorder && recorder.state === 'recording') {
            try {
                recorder.pause();
            } catch (err) {
                console.warn('[SoloSingleFileScreenRecording] Failed to pause:', err);
            }
        }
        if (this.audioContext) {
            try {
                await this.audioContext.suspend();
            } catch { }
        }
    }

    /**
     * Resume recording after a pause (best-effort).
     */
    public async resume(): Promise<void> {
        if (this.state !== 'recording') return;
        if (this.audioContext) {
            try {
                await this.audioContext.resume();
            } catch { }
        }
        const recorder = this.mediaRecorder;
        if (recorder && recorder.state === 'paused') {
            try {
                recorder.resume();
            } catch (err) {
                console.warn('[SoloSingleFileScreenRecording] Failed to resume:', err);
            }
        }
    }

    /**
     * Check if the underlying MediaRecorder is paused.
     */
    public isPaused(): boolean {
        return this.mediaRecorder?.state === 'paused';
    }

    public isRecording(): boolean {
        return this.state === 'recording';
    }

    public getTrackId(): string {
        return this.trackId;
    }

    public getFilePath(): string {
        return this.opfsPath;
    }

    public async cleanup(): Promise<void> {
        if (this.state === 'recording') {
            try {
                await this.stop();
            } catch { }
        }

        await deleteOpfsFile(this.opfsPath);
        console.log('[SoloSingleFileScreenRecording] Cleaned up:', this.opfsPath);
    }
}
